import { ThreeEvent } from '@react-three/fiber';
import { FunctionComponent, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DoubleSide, FrontSide } from 'three';
import { IDesignerState } from '../../../../typings';
import FloatingInput from '../FloatingInput/FloatingInput';
import SelectedEntityPositionHotspot from '../Hotspots/SelectedEntityPositionHotspot';
import LineBox from '../LineBox/LineBox';
import { IText, ITextReactProps, ITuple3 } from '../r3f-components/component-data-structure';
import CustomBillboard from '../r3f-components/components/CustomBillboard/CustomBillboard';
import { getUserDataFromIntersections, isHighestRenderOrder } from '../r3f-components/utils/general';
import EntityBoundaries from './EntityBoundaries/EntityBoundaries';
import { useGetHocCurvedProperties, useGetHocSpatialProperties, useGetHocTextProperties, useHocEntityPointerMove, useHocPointerDown, useHocPointerMove, useHocPointerOut, useHocPointerOver, useHocPointerUp, useShowHighlight } from './hocCustomHooks';

interface ITextWrapped {
	id: string;
	enabled?: boolean;
}

const withTextBehaviour = (WrappedText: FunctionComponent<ITextReactProps>): FunctionComponent<ITextWrapped> => {
	const FuncComp: FunctionComponent<ITextWrapped> = ({ enabled: entityIsEnabled = true, id }) => {
		// Selectors
		const showFloatingInput = useSelector((state: IDesignerState) => state.userReducer.selectedEntityIds?.length);
		const entity = useSelector((state: IDesignerState) => state.contentReducer.contentDoc.componentsById[id] as IText);
		const projectShadowsEnabled = useSelector((state: IDesignerState) => state.contentReducer.contentDoc.shadowsEnabled ?? true);
        const is3dMode = useSelector((state: IDesignerState) => state.userReducer.is3dMode)

		// Refs
		const pointerStageRef = useRef<'down' | 'move' | 'up' | null>(null);
		const shiftLockedRef = useRef<number | null>(null);
		const initialPosRef = useRef<ITuple3 | null>(null); // Used to calculate pointer offset in pointerMove
		const previousPositionRef = useRef<ITuple3 | null>(null); // The previous position, used to compare with the current position
		const pointerUpHasRunRef = useRef<boolean | null>(null); // To prevent multiple dispatched actions when pointerUp runs twice this is reset onPointerMove
		// const canvasRef = useRef<HTMLCanvasElement>(document.getElementById(IDomIdSelectors.zapparCanvas) as HTMLCanvasElement);
		const [doubleClicked, setDoubleClicked] = useState(false); // For text / button entities, to display FloatingInput
		const [showEntityPositionHotspot, setShowEntityPositionHotspot] = useState(false);
		const [isHovered, setIsHovered] = useState(false);

		// Entity properties to pass to wrapped component
		const { opacity, text, fontFamily, fontSize, textAlignment, castShadow = false, receiveShadow = false, billboard, depthTest = true, renderOrder } = entity;
		const { scale, position, rotation } = useGetHocSpatialProperties(entity);
		const { fontRgba, color } = useGetHocTextProperties(entity);
		const { curvature } = useGetHocCurvedProperties(entity);

		// Pointer events
		const onPointerDown = useHocPointerDown({ pointerStageRef, initialPosRef, entity, setShowEntityPositionHotspot });
		const onPointerMove = useHocPointerMove({ pointerStageRef, initialPosRef, pointerUpHasRunRef, previousPositionRef, entity, shiftLockedRef });
		const onPointerUp = useHocPointerUp({ pointerStageRef, pointerUpHasRunRef, initialPosRef, entity, setShowEntityPositionHotspot, shiftLockedRef });
        const onPointerOver = useHocPointerOver(entity, () => setIsHovered(true));
        const onPointerOut = useHocPointerOut(entity, () => setIsHovered(false));
        const onEntityPointerMove = useHocEntityPointerMove(entity, () => setIsHovered(false), () => setIsHovered(true));
        const showHighlight = useShowHighlight(id, isHovered);

		const onDoubleClick = (e: ThreeEvent<MouseEvent>) => {
			if (!isHighestRenderOrder({ userDataArray: getUserDataFromIntersections(e.intersections), renderOrder })) return;
			setDoubleClicked(true);
		};

		const floatingInput =
			showFloatingInput && doubleClicked ? <FloatingInput position={position} text={entity.text} onBlur={() => setDoubleClicked(false)} onUnmount={() => setDoubleClicked(false)} /> : undefined;
        
		return (
			<>
				<EntityBoundaries
					position={position}
					rotation={rotation}
					scale={scale}
					renderOrder={renderOrder}
					onPointerDown={entityIsEnabled ? onPointerDown : undefined}
					onPointerUp={onPointerUp}
					label={`Text component ${id} render order ${renderOrder}`}
					floatingInput={floatingInput}
				>
                    <>
                        <WrappedText
                            id={id}
                            scale={scale}
                            rotation={rotation}
                            position={position}
                            renderOrder={renderOrder}
                            opacity={opacity}
                            textAlignment={textAlignment}
                            fontRgba={fontRgba}
                            fontFamily={fontFamily}
                            fontSize={fontSize}
                            text={text}
                            color={color as any}
                            side={entity.isSnappedToTarget ? FrontSide :  DoubleSide}
                            curvature={curvature}
                            castShadow={castShadow && projectShadowsEnabled}
                            receiveShadow={receiveShadow && projectShadowsEnabled}
                            depthTest={depthTest}
                            onPointerDown={entityIsEnabled ? onPointerDown : undefined}
                            onPointerUp={entityIsEnabled ? onPointerUp : undefined}
                            onPointerOver={entityIsEnabled ? onPointerOver : undefined}
                            onPointerOut={entityIsEnabled ? onPointerOut : undefined}
                            onDoubleClick={entityIsEnabled ? onDoubleClick : undefined}
                            onPointerMove={entityIsEnabled ? onEntityPointerMove : undefined}
                            billboard={billboard}
                            isCameraOrbiting
                            rotateNoBillboardOuterGroup
                        />
                        <CustomBillboard 
                            position={position}
                            rotation={rotation}
                            follow={!!billboard?.[0]}
                            lockY={!billboard?.[1]}
                            visible={showHighlight}
                            isCameraOrbiting
                            rotateNoBillboardOuterGroup
                        >
                            <LineBox renderOrder={renderOrder} rotation={[0, 0 , 0]} scale={[scale[0] / 2, scale[1] / 2, scale[2] / 2]} color={[66, 135, 245, 1]} lineWidth={6} />
                        </CustomBillboard>
                    </>
				</EntityBoundaries>
				{is3dMode && showEntityPositionHotspot && <SelectedEntityPositionHotspot onPointerMove={showEntityPositionHotspot ? onPointerMove : undefined} onPointerUp={entityIsEnabled ? onPointerUp : undefined} />}
			</>
		);
	};
	return FuncComp;
};

export default withTextBehaviour;
